import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';
import { Tabs, Tab, Box } from '@mui/material';
import { MAIN_THEME } from '@styles';
import { CDN_URL } from '@utils';
import { ITabsProps } from '..';

interface ITabsWidgetsProps {
  mainPageName: string;
  tabs: ITabsProps[];
  tabPanelClassName?: string;
  onTabChange?: (newTab: string) => void;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '1.5em',
  },
  tabPane: {
    display: 'flex',
    color: MAIN_THEME.color.black,
    alignItems: 'center',
    justifyContent: 'flex-start',
    minWidth: '90%',
  },
  tabIcon: {
    width: '18px',
    height: '18px',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundColor: 'transparent',
    backgroundPosition: 'center',
    margin: 0,
    float: 'left',
    marginRight: '10px',
  },
  countNumber: {
    height: '20px',
    width: '20px',
    border: 'none',
    borderRadius: '50%',
    backgroundColor: MAIN_THEME.color.white92,
    margin: '0 5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: MAIN_THEME.fontSize.xs,
  },
  tabBox: {
    '& .MuiTabs-indicator': {
      background: MAIN_THEME.color.black,
      height: '3px',
    },
  },
  tabs: {
    minWidth: '185px',
  },
  tabTitle: {
    fontSize: MAIN_THEME.fontSize.rg,
    fontFamily: MAIN_THEME.font.regular,
    textTransform: 'none',
    lineHeight: '24px',
  },
  tabActiveTitle: {
    fontFamily: MAIN_THEME.font.medium,
    fontWeight: 'bold',
  },
  tabPanel: {
    padding: '0',
    marginTop: '25px',
  },
}));

const TabsWidgets: React.FC<ITabsWidgetsProps> = ({
  tabs = [],
  tabPanelClassName,
  onTabChange,
}) => {
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.name || '');

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  return (
    <section className={classes.container}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        {/* TABS (HEADER) */}
        <Tabs
          className={classes.tabBox}
          value={activeTab}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab) => (
            <Tab
              className={classes.tabs}
              key={tab.name}
              value={tab.name}
              label={
                <div className={classes.tabPane}>
                  <figure
                    className={classes.tabIcon}
                    style={{
                      backgroundImage: tab.iconUrl
                        ? `url(${CDN_URL}${tab.iconUrl})`
                        : `url(${CDN_URL}/icons/file-summary-primary.svg)`,
                    }}
                  />
                  <span
                    className={clsx(classes.tabTitle, {
                      [classes.tabActiveTitle]: tab.name === activeTab,
                    })}
                  >
                    {tab.title}
                  </span>
                  {tab.count && (
                    <div className={classes.countNumber}>{tab.count}</div>
                  )}
                </div>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* TAB PANELS (CONTENT) */}
      {tabs.map((tab) => {
        // Render each tab's widgets if it matches the active tab
        if (tab.name !== activeTab) return null;

        return tab.widgets.map(
          (widget: { children: React.ReactNode }, idx: number) => (
            <Box
              key={`${tab.name}_${idx}`}
              className={clsx(classes.tabPanel, tabPanelClassName)}
            >
              {widget.children}
            </Box>
          )
        );
      })}
    </section>
  );
};

export default TabsWidgets;
